import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import GenerationLog from '../components/GenerationLog';
import AdhocFormSender from '../components/AdhocFormSender';
import AdhocRequestList from '../components/AdhocRequestList';
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const STATUS_COLORS = {
  NOT_STARTED: '#9CA3AF',
  OPENED: '#3B82F6',
  IN_PROGRESS: '#F59E0B',
  COMPLETED: '#10B981',
  EXPIRED: '#EF4444',
};

const STATUS_LABELS = {
  NOT_STARTED: 'Not Started',
  OPENED: 'Opened',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired',
};

const PIE_COLORS = ['#9CA3AF', '#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

function IntakeDashboard({ practiceId }) {
  const [view, setView] = useState('dashboard'); // dashboard | generation-log | adhoc
  const [adhocRefreshKey, setAdhocRefreshKey] = useState(0);
  const [dateRange, setDateRange] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [activeForms, setActiveForms] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  // Table state
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('DESC');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterApptType, setFilterApptType] = useState('');
  const [filterAdhoc, setFilterAdhoc] = useState('');
  const [search, setSearch] = useState('');
  const [apptTypeOptions, setApptTypeOptions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [reminding, setReminding] = useState(false);
  const [jsonModal, setJsonModal] = useState(null); // { row, data, loading }

  const LIMIT = 25;

  const getDateParams = useCallback(() => {
    if (dateRange === 'custom') {
      return { date_from: customFrom, date_to: customTo };
    }
    // Intake Command Center is forward-looking — practice admins chase
    // paperwork BEFORE the appointment. The 7d/30d/90d buttons now mean
    // "next N days" (was lookback, which surfaced past appts with no
    // actionable value). KPI / chart endpoints still receive the same
    // window for trend continuity, just shifted into the future.
    const now = new Date();
    const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;
    const to = new Date(now.getTime() + days * 86400000);
    return { date_from: now.toISOString(), date_to: to.toISOString() };
  }, [dateRange, customFrom, customTo]);

  const loadDashboard = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const params = getDateParams();
      const [kpiRes, chartRes, alertRes] = await Promise.all([
        api.getIntakeDashboardKPIs(practiceId, params),
        api.getIntakeDashboardCharts(practiceId, { ...params, granularity: dateRange === '7d' ? 'day' : 'day' }),
        api.getIntakeDashboardAlerts(practiceId),
      ]);
      if (kpiRes.success) setKpis(kpiRes.data);
      if (chartRes.success) setCharts(chartRes.data);
      if (alertRes.success) setAlerts(alertRes.data);
    } catch (err) {
      console.error('Error loading intake dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, getDateParams, dateRange]);

  const loadActiveForms = useCallback(async () => {
    if (!practiceId) return;
    try {
      setTableLoading(true);
      const params = {
        sortBy, sortDir, limit: LIMIT, offset: page * LIMIT,
        ...(filterStatus && { status: filterStatus }),
        ...(filterApptType && { appointmentType: filterApptType }),
        ...(filterAdhoc !== '' && { isAdhoc: filterAdhoc }),
        ...(search && { search }),
      };
      const res = await api.getIntakeDashboardActiveForms(practiceId, params);
      if (res.success) setActiveForms(res.data);
    } catch (err) {
      console.error('Error loading active forms:', err);
    } finally {
      setTableLoading(false);
    }
  }, [practiceId, sortBy, sortDir, page, filterStatus, filterApptType, filterAdhoc, search]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadActiveForms(); }, [loadActiveForms]);

  // GH-182: real appointment types come from actual appointments, not a
  // hardcoded list. Fetch once per practice so the filter dropdown reflects
  // the practice's real inventory ("New Sleep 30", "Follow-Up Telemed 45", …).
  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    api.request(`/practices/${practiceId}/intake/dashboard/appointment-types`)
      .then(res => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) setApptTypeOptions(res.data);
      })
      .catch(() => { /* dropdown falls back to just "All Appt Types" */ });
    return () => { cancelled = true; };
  }, [practiceId]);

  const handleExport = async () => {
    try {
      // The api method returns the response Blob with the server's
      // text/csv;charset=utf-8 type and the UTF-8 BOM intact. Don't re-wrap
      // it in another Blob — that can drop the charset hint and confuse
      // Excel-on-Windows. (commons GH-44 follow-up)
      const blob = await api.exportIntakeDashboardCSV(practiceId, getDateParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intake-forms-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleBulkRemind = async () => {
    if (selected.size === 0) return;
    try {
      setReminding(true);
      const res = await api.sendIntakeBulkRemind(practiceId, Array.from(selected));
      if (res.success) {
        const { sent = 0, failed = 0, skipped = 0, errors = [] } = res.data || {};
        const parts = [`Reminders sent: ${sent}`];
        if (failed > 0) parts.push(`failed: ${failed}`);
        if (skipped > 0) parts.push(`skipped: ${skipped}`);
        let msg = parts.join(' | ');
        if (errors.length > 0) {
          const sample = errors.slice(0, 3).map(e => `• ${e.error}`).join('\n');
          msg += `\n\nFirst issues:\n${sample}`;
        }
        alert(msg);
        if (sent > 0) setSelected(new Set());
      } else {
        alert(`Bulk remind failed: ${res.error || 'unknown error'}`);
      }
    } catch (err) {
      console.error('Bulk remind failed:', err);
      alert(`Bulk remind failed: ${err.message}`);
    } finally {
      setReminding(false);
    }
  };

  const viewFormJson = async (row) => {
    setJsonModal({ row, data: null, loading: true });
    try {
      const url = row.id
        ? `/practices/${practiceId}/intake/dashboard/form-json-by-id/${row.id}`
        : `/practices/${practiceId}/intake/dashboard/form-json/${row.patient_id}/${row.appointment_id}`;
      const res = await api.request(url);
      setJsonModal({ row, data: res.data?.form_json || res.data || res, loading: false });
    } catch (err) {
      setJsonModal({ row, data: { error: err.message }, loading: false });
    }
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortDir('DESC');
    }
    setPage(0);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === activeForms.rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(activeForms.rows.map(r => r.id)));
    }
  };

  if (loading && !kpis) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><p>Loading Intake Command Center...</p></div>;
  }

  const totalPages = Math.ceil((activeForms.total || 0) / LIMIT);
  const trendData = (charts?.trend || []).map(d => ({
    ...d,
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* View Tabs */}
      <div style={s.viewTabs}>
        {[
          { key: 'dashboard', label: 'Dashboard' },
          { key: 'generation-log', label: 'Generation Log' },
          { key: 'adhoc', label: 'Ad-Hoc Forms' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setView(tab.key)}
            style={{ ...s.viewTab, ...(view === tab.key ? s.viewTabActive : {}) }}>
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'generation-log' && <GenerationLog practiceId={practiceId} />}
      {view === 'adhoc' && (
        <div style={{ marginTop: 8 }}>
          <AdhocFormSender practiceId={practiceId} onSent={() => setAdhocRefreshKey(k => k + 1)} />
          <AdhocRequestList practiceId={practiceId} refreshKey={adhocRefreshKey} />
        </div>
      )}

      {view === 'dashboard' && <>
      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>Intake Command Center</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {['7d', '30d', '90d'].map(r => (
            <button key={r} onClick={() => { setDateRange(r); }}
              style={{ ...s.rangeBtn, ...(dateRange === r ? s.rangeBtnActive : {}) }}
              title="Forward-looking — next N days">
              Next {r}
            </button>
          ))}
          <button onClick={() => setDateRange('custom')}
            style={{ ...s.rangeBtn, ...(dateRange === 'custom' ? s.rangeBtnActive : {}) }}>
            Custom
          </button>
          {dateRange === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={s.dateInput} />
              <span style={{ color: '#6B7280' }}>to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={s.dateInput} />
              <button onClick={loadDashboard} style={s.applyBtn}>Apply</button>
            </>
          )}
          <button onClick={handleExport} style={s.exportBtn}>Export CSV</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={s.kpiGrid}>
        <KPICard label="Total Forms" value={kpis?.total_forms || 0} icon="📋" />
        <KPICard label="Completion Rate" value={`${kpis?.completion_rate || 0}%`} icon="✅" color="#10B981" />
        <KPICard label="Avg Completion Time" value={formatTime(kpis?.avg_time_seconds)} icon="⏱" color="#3B82F6" />
        <KPICard label="Pending" value={kpis?.pending || 0} icon="⏳" color="#F59E0B" />
        <KPICard label="Overdue" value={kpis?.overdue || 0} icon="🚨" color={kpis?.overdue > 0 ? '#EF4444' : '#6B7280'} sublabel="all-time" />
      </div>

      {/* Charts Row 1 */}
      <div style={s.chartRow}>
        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>Completion Trend</h3>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                <Area type="monotone" dataKey="total" name="Total" stroke="#6B7280" fill="#F3F4F6" />
                <Area type="monotone" dataKey="completed" name="Completed" stroke="#10B981" fill="#D1FAE5" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p style={s.noData}>No trend data for selected range</p>
          )}
        </div>

        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>Status Breakdown</h3>
          {(charts?.statusBreakdown || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={(charts?.statusBreakdown || []).map(d => ({
                    name: STATUS_LABELS[d.status] || d.status,
                    value: parseInt(d.count),
                    fill: STATUS_COLORS[d.status] || '#6B7280',
                  }))}
                  cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                  dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {(charts?.statusBreakdown || []).map((d, i) => (
                    <Cell key={i} fill={STATUS_COLORS[d.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p style={s.noData}>No status data available</p>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={s.chartRow}>
        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>Drop-Off by Section</h3>
          {(charts?.dropOff || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts.dropOff} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="section" tick={{ fontSize: 11 }} width={120} />
                <RechartsTooltip />
                <Bar dataKey="count" name="Drop-offs" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={s.noData}>No drop-off data available</p>
          )}
        </div>

        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>By Appointment Type</h3>
          {(charts?.byAppointmentType || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts.byAppointmentType}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="appointment_type" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                {/* GH-73: previously #E5E7EB — near-invisible against the
                    white card background. #6B7280 keeps the gray theme but
                    actually reads as a bar. */}
                <Bar dataKey="total" name="Total" fill="#6B7280" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={s.noData}>No appointment type data available</p>
          )}
        </div>
      </div>

      {/* Alerts Panel */}
      {alerts && (alerts.approachingAppointment?.length > 0 || alerts.staleForms?.length > 0 || alerts.highDropOff?.length > 0) && (
        <div style={s.alertsPanel}>
          <h3 style={s.chartTitle}>Smart Alerts</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            {/* Approaching Appointments */}
            <div>
              <h4 style={s.alertHeader}>
                <span style={{ ...s.alertDot, background: '#EF4444' }} />
                Approaching Appointments ({alerts.approachingAppointment?.length || 0})
              </h4>
              {(alerts.approachingAppointment || []).slice(0, 5).map(a => (
                <div key={a.id} style={s.alertItem}>
                  <div style={{ fontWeight: 500 }}>{a.patient_name}</div>
                  <div style={s.alertMeta}>
                    {a.days_until === 0 ? 'Today' : a.days_until === 1 ? 'Tomorrow' : `In ${a.days_until} days`}
                    {' · '}{a.completion_pct}% complete
                  </div>
                </div>
              ))}
            </div>

            {/* Stale Forms */}
            <div>
              <h4
                style={s.alertHeader}
                title="Forms that are OPENED or IN_PROGRESS and have had no patient activity for more than 48 hours."
              >
                <span style={{ ...s.alertDot, background: '#F59E0B' }} />
                Stale Forms ({alerts.staleForms?.length || 0})
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>
                  · inactive &gt; 48h
                </span>
              </h4>
              {(alerts.staleForms || []).slice(0, 5).map(a => (
                <div key={a.id} style={s.alertItem}>
                  <div style={{ fontWeight: 500 }}>{a.patient_name}</div>
                  <div style={s.alertMeta}>
                    {a.hours_inactive}h inactive · {a.completion_pct}% complete
                  </div>
                </div>
              ))}
            </div>

            {/* High Drop-Off */}
            <div>
              <h4 style={s.alertHeader}>
                <span style={{ ...s.alertDot, background: '#8B5CF6' }} />
                High Drop-Off Sections ({alerts.highDropOff?.length || 0})
              </h4>
              {(alerts.highDropOff || []).map(a => (
                <div key={a.section} style={s.alertItem}>
                  <div style={{ fontWeight: 500 }}>{a.section}</div>
                  <div style={s.alertMeta}>{a.count} forms · {a.pct}% of incomplete</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Active Forms Table */}
      <div style={s.tableCard}>
        <div style={s.tableHeader}>
          <h3 style={s.chartTitle}>Active Forms</h3>
          {selected.size > 0 && (
            <button onClick={handleBulkRemind} disabled={reminding} style={s.remindBtn}>
              {reminding ? 'Sending...' : `Send Reminder (${selected.size})`}
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={s.filters}>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }} style={s.filterSelect}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select value={filterApptType} onChange={e => { setFilterApptType(e.target.value); setPage(0); }} style={s.filterSelect}>
            <option value="">All Appt Types</option>
            {apptTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.value}{opt.count ? ` (${opt.count})` : ''}
              </option>
            ))}
          </select>
          <select value={filterAdhoc} onChange={e => { setFilterAdhoc(e.target.value); setPage(0); }} style={s.filterSelect}>
            <option value="">All Types</option>
            <option value="0">Scheduled</option>
            <option value="1">Ad-Hoc</option>
          </select>
          <input
            type="text" placeholder="Search patients..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            style={s.searchInput}
          />
        </div>

        {tableLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>Loading...</div>
        ) : (
          <>
            <table style={s.table}>
              <thead>
                <tr style={s.tableHeaderRow}>
                  <th style={s.th}>
                    <input type="checkbox" checked={selected.size === activeForms.rows.length && activeForms.rows.length > 0}
                      onChange={toggleSelectAll} />
                  </th>
                  <SortTh label="Patient" col="patient_name" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortTh label="Appt Date" col="appointment_date" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <th style={s.th}>Type</th>
                  <SortTh label="Status" col="status" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortTh label="Completion" col="completion_pct" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <th style={s.th}>Section</th>
                  <SortTh label="Last Activity" col="last_activity_at" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeForms.rows.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>No forms found</td></tr>
                ) : activeForms.rows.map(row => (
                  <tr key={row.id} style={s.tableRow}>
                    <td style={s.td}>
                      <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
                    </td>
                    <td style={{ ...s.td, fontWeight: 500 }}>{row.patient_name || 'Unknown'}</td>
                    <td style={s.td}>{row.appointment_date ? (() => { const pts = String(row.appointment_date).split('T')[0].split('-').map(Number); return pts.length === 3 ? new Date(Date.UTC(pts[0], pts[1]-1, pts[2], 12)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : row.appointment_date; })() : '—'}</td>
                    <td style={s.td}>
                      {row.is_adhoc ? <span style={s.adhocBadge}>Ad-Hoc</span> : (row.appointment_type || '—')}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, background: STATUS_COLORS[row.status] || '#9CA3AF' }}>
                        {STATUS_LABELS[row.status] || row.status}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={s.progressContainer}>
                        <div style={{ ...s.progressBar, width: `${row.completion_pct || 0}%` }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{row.completion_pct || 0}%</span>
                    </td>
                    <td style={{ ...s.td, fontSize: '0.8rem' }}>
                      {/* COMPLETED forms shouldn't render a "stuck on
                          Personal Details" label. NHIntake doesn't always
                          update current_section after the final submit,
                          so it stays frozen on whatever section the
                          patient last triggered a save from. Show "—"
                          once the form is done so the column reads as
                          "no longer applicable" rather than misleading
                          progress info. */}
                      {row.status === 'COMPLETED' ? '—' : (row.current_section || '—')}
                    </td>
                    <td style={{ ...s.td, fontSize: '0.8rem', color: '#6B7280' }}>
                      {row.last_activity_at ? timeAgo(row.last_activity_at) : '—'}
                    </td>
                    <td style={s.td}>
                      <button onClick={() => viewFormJson(row)} style={s.jsonBtn}>View JSON</button>
                      {['IN_PROGRESS', 'COMPLETED'].includes(row.status) && (row.generation_log_id || row.appointment_id) && (
                        <button onClick={async () => {
                          if (!window.confirm('Push current intake data to EHR? This sends whatever has been filled so far.')) return;
                          try {
                            const pushUrl = row.generation_log_id
                              ? `/practices/${practiceId}/intake/generations/${row.generation_log_id}/push-to-ehr`
                              : `/practices/${practiceId}/intake/${row.appointment_id}/push-to-ehr`;
                            await api.post(pushUrl);
                            alert('Intake data pushed to EHR successfully');
                          } catch (err) { alert('Push failed: ' + (err.message || 'Unknown error')); }
                        }} style={{ ...s.jsonBtn, marginLeft: 4, background: '#059669', color: '#fff' }}>Push to EHR</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={s.pagination}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={s.pageBtn}>Previous</button>
                <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                  Page {page + 1} of {totalPages} ({activeForms.total} total)
                </span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={s.pageBtn}>Next</button>
              </div>
            )}
          </>
        )}
      </div>

      </>}

      {/* JSON Viewer Modal */}
      {jsonModal && (
        <div style={s.overlay} onClick={() => setJsonModal(null)}>
          <div style={s.jsonModal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>
                Form JSON — {jsonModal.row?.patient_name || 'Unknown'}
              </h3>
              <button onClick={() => setJsonModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>x</button>
            </div>
            {jsonModal.loading ? (
              <p style={{ color: '#6B7280' }}>Loading form data...</p>
            ) : (
              <pre style={s.jsonPre}>{JSON.stringify(jsonModal.data, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -- Helper components --

function KPICard({ label, value, icon, color = '#374151', sublabel }) {
  return (
    <div style={s.kpiCard}>
      <div style={{ fontSize: '0.8rem', color: '#6B7280', marginBottom: '0.25rem' }}>{icon} {label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
      {sublabel && <div style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: '0.15rem' }}>{sublabel}</div>}
    </div>
  );
}

function SortTh({ label, col, current, dir, onClick }) {
  const arrow = current === col ? (dir === 'ASC' ? ' ↑' : ' ↓') : '';
  return (
    <th style={{ ...s.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => onClick(col)}>
      {label}{arrow}
    </th>
  );
}

// -- Utility functions --

function formatTime(seconds) {
  if (!seconds) return '0m';
  const m = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (m === 0) return `${sec}s`;
  return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// -- Styles --

const s = {
  viewTabs: { display: 'flex', gap: 4, marginBottom: '1.25rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 8 },
  viewTab: { padding: '8px 16px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500 },
  viewTabActive: { background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' },
  title: { fontSize: '1.5rem', fontWeight: 600, color: '#111827' },
  rangeBtn: { padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 },
  rangeBtnActive: { background: '#2563EB', color: '#fff', borderColor: '#2563EB' },
  dateInput: { padding: '5px 8px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '0.8rem' },
  applyBtn: { padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 },
  exportBtn: { padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, marginLeft: '0.5rem' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  kpiCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1rem 1.25rem' },

  chartRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' },
  chartCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' },
  chartTitle: { fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' },
  noData: { color: '#9CA3AF', fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' },

  alertsPanel: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem' },
  alertHeader: { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' },
  alertDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%' },
  alertItem: { padding: '0.4rem 0', borderBottom: '1px solid #F3F4F6', fontSize: '0.8rem' },
  alertMeta: { fontSize: '0.75rem', color: '#6B7280' },

  tableCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  remindBtn: { padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 },
  filters: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' },
  filterSelect: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '0.8rem', background: '#fff' },
  searchInput: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '0.8rem', minWidth: '180px' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
  tableHeaderRow: { borderBottom: '2px solid #E5E7EB' },
  th: { padding: '0.6rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' },
  tableRow: { borderBottom: '1px solid #F3F4F6' },
  td: { padding: '0.6rem 0.5rem', color: '#374151' },

  statusBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', color: '#fff', fontSize: '0.7rem', fontWeight: 600 },
  adhocBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', background: '#EDE9FE', color: '#7C3AED', fontSize: '0.7rem', fontWeight: 600 },
  progressContainer: { width: '60px', height: 6, background: '#F3F4F6', borderRadius: 3, display: 'inline-block', marginRight: 6, verticalAlign: 'middle' },
  progressBar: { height: '100%', background: '#10B981', borderRadius: 3, transition: 'width 0.3s' },

  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', padding: '1rem 0' },
  pageBtn: { padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: '0.8rem' },

  jsonBtn: { padding: '3px 8px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#F9FAFB', cursor: 'pointer', fontSize: '0.7rem', color: '#374151' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  jsonModal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 900, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  jsonPre: { background: '#1F2937', color: '#D1D5DB', padding: 16, borderRadius: 8, fontSize: 12, lineHeight: 1.5, overflow: 'auto', maxHeight: '60vh', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
};

export default IntakeDashboard;
